import React, { useEffect, useRef, useState, useCallback } from "react";
import { Info, X } from "lucide-react";

/**
 * Autosave an in-progress "create" form to localStorage so nothing is lost on
 * an accidental refresh. Create-only (never for editing existing records).
 *
 * @param {Object}   opts
 * @param {string}   opts.storageKey  Unique localStorage key for this form.
 * @param {Object}   opts.data        Serializable snapshot of the current form state.
 * @param {boolean}  opts.hasContent  Whether the form has meaningful content to save.
 * @param {Function} opts.onRestore   Called once on mount with the parsed draft.
 * @param {boolean}  [opts.enabled]   Set false to disable (e.g. edit mode).
 */
export function useFormDraft({ storageKey, data, hasContent, onRestore, enabled = true }) {
  const [draftRestored, setDraftRestored] = useState(false);
  const restoredRef = useRef(false);

  // Restore once on mount.
  useEffect(() => {
    if (!enabled) {
      restoredRef.current = true;
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        onRestore(parsed);
        setDraftRestored(true);
      }
    } catch (e) {
      /* ignore malformed draft */
    }
    restoredRef.current = true;
  }, []);

  // Persist on change (debounced). Only after the restore attempt has run.
  const serialized = JSON.stringify(data);
  useEffect(() => {
    if (!enabled || !restoredRef.current) return;
    const t = setTimeout(() => {
      try {
        if (hasContent) localStorage.setItem(storageKey, serialized);
        else localStorage.removeItem(storageKey);
      } catch (e) {
        /* ignore quota errors */
      }
    }, 500);
    return () => clearTimeout(t);
  }, [serialized, hasContent, enabled, storageKey]);

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      /* ignore */
    }
    setDraftRestored(false);
  }, [storageKey]);

  const dismissBanner = useCallback(() => setDraftRestored(false), []);

  return { draftRestored, clearDraft, dismissBanner };
}

export const DraftRestoredBar = ({ onDiscard, onDismiss }) => (
  <div
    data-testid="draft-restored-bar"
    className="flex items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800"
  >
    <span className="flex items-center gap-2">
      <Info className="h-4 w-4 shrink-0" />
      Draft restored from your last unsaved session
    </span>
    <span className="flex items-center gap-3">
      <button
        type="button"
        onClick={onDiscard}
        className="font-medium underline hover:no-underline"
        data-testid="draft-discard-btn"
      >
        Discard draft
      </button>
      {onDismiss && (
        <button type="button" onClick={onDismiss} data-testid="draft-dismiss-btn" aria-label="Dismiss">
          <X className="h-4 w-4" />
        </button>
      )}
    </span>
  </div>
);
